import { useRef } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

export type CreatedAccount = {
  externalId: string;
  loginId: string;
  displayName: string;
};

export type InviteResponse = {
  autoJoinDomain: string;
  email: string;
  invitationToken: string;
  createdAccount?: CreatedAccount;
  name: string;
  warehouseNative: boolean;
};

export function useInvite(inviteId?: string): [
  {
    loading: boolean;
    error?: Error;
    value?: InviteResponse;
  },
  (signal?: AbortSignal) => Promise<InviteResponse | undefined>,
] {
  const resolvedInviteId = useRef<string | undefined>(undefined);

  const [state, getAccountInvite] = useAsyncFn(
    async (signal?: AbortSignal) => {
      if (!inviteId) {
        resolvedInviteId.current = undefined;
        return undefined;
      }

      try {
        const response = await fetch(
          `https://onboarding.confidence.dev/v1/accountInvites/${encodeURIComponent(
            inviteId,
          )}`,
          { signal },
        );
        const json = await response.json();
        if (signal?.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        if (!response.ok) {
          throw new Error(json.message);
        }
        resolvedInviteId.current = inviteId;
        return json as InviteResponse;
      } catch (error) {
        if (!signal?.aborted) {
          resolvedInviteId.current = inviteId;
        }
        throw error;
      }
    },
    [inviteId],
    { loading: Boolean(inviteId) },
  );

  const isResolvedForCurrentInvite = resolvedInviteId.current === inviteId;

  return [
    {
      loading: Boolean(inviteId) && !isResolvedForCurrentInvite,
      error: isResolvedForCurrentInvite ? state.error : undefined,
      value: isResolvedForCurrentInvite ? state.value : undefined,
    },
    getAccountInvite,
  ];
}
